import { Tooltip } from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import flashFill from "@iconify/icons-eva/flash-fill";
import Iconify from "src/components/iconify";
import PropTypes from "prop-types";
import { propuestasApi } from "src/services/propuestasApi";
import { useSnackbar } from "src/components/snackbar";

/**
 * Botón que genera un resumen ejecutivo automático usando IA (GPT-4o).
 * Requiere al menos `titulo` y `descripcion` para activarse.
 *
 * Props:
 *   titulo              — título del proyecto
 *   descripcion         — descripción del proyecto
 *   antecedentes        — antecedentes ya ingresados (opcional)
 *   resumenEjecutivo — borrador actual del resumen ejecutivo (opcional, se envía al LLM como contexto)
 *   fases               — array de { titulo, objetivo, monto } (opcional)
 *   montoTotal          — inversión total (opcional)
 *   duracionEstimada    — duración estimada del proyecto (opcional)
 *   nombreCliente       — nombre del contacto cliente (opcional)
 *   empresaCliente      — nombre de la empresa cliente (opcional)
 *   onGenerated         — callback(resumen) que inserta el texto en el campo
 *   disabled            — deshabilita el botón
 */
const GenerarResumenEjecutivoButton = ({
  titulo,
  descripcion,
  antecedentes,
  resumenEjecutivo,
  fases,
  montoTotal,
  duracionEstimada,
  nombreCliente,
  empresaCliente,
  clientes,
  onGenerated,
  disabled,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [generarResumenEjecutivo, { isLoading }] =
    propuestasApi.useGenerarResumenEjecutivoMutation();

  const puedeGenerar =
    titulo && titulo.trim() !== "" && descripcion && descripcion.trim() !== "";

  const handleClick = async () => {
    if (!puedeGenerar) return;

    try {
      const resultado = await generarResumenEjecutivo({
        titulo,
        descripcion,
        ...(antecedentes ? { antecedentes } : {}),
        ...(resumenEjecutivo ? { resumenEjecutivo } : {}),
        ...(fases && fases.length > 0 ? { fases } : {}),
        ...(montoTotal ? { montoTotal } : {}),
        ...(duracionEstimada ? { duracionEstimada } : {}),
        ...(nombreCliente ? { nombreCliente } : {}),
        ...(empresaCliente ? { empresaCliente } : {}),
        ...(clientes && clientes.length > 0 ? { clientes } : {}),
      }).unwrap();

      onGenerated(resultado.resumen);
    } catch {
      enqueueSnackbar(
        "No se pudo generar el resumen ejecutivo. Intenta nuevamente.",
        {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        },
      );
    }
  };

  return (
    <Tooltip
      title={
        puedeGenerar
          ? "Generar resumen ejecutivo con IA"
          : "Completa el título y la descripción para generar el resumen"
      }
    >
      <span>
        <LoadingButton
          size="small"
          variant="outlined"
          color="secondary"
          loading={isLoading}
          disabled={disabled || !puedeGenerar}
          onClick={handleClick}
          startIcon={<Iconify icon={flashFill} width={16} height={16} />}
        >
          Generar con IA
        </LoadingButton>
      </span>
    </Tooltip>
  );
};

GenerarResumenEjecutivoButton.propTypes = {
  titulo: PropTypes.string,
  descripcion: PropTypes.string,
  antecedentes: PropTypes.string,
  resumenEjecutivo: PropTypes.string,
  fases: PropTypes.arrayOf(
    PropTypes.shape({
      titulo: PropTypes.string,
      objetivo: PropTypes.string,
      monto: PropTypes.string,
    }),
  ),
  montoTotal: PropTypes.string,
  duracionEstimada: PropTypes.string,
  nombreCliente: PropTypes.string,
  empresaCliente: PropTypes.string,
  clientes: PropTypes.arrayOf(
    PropTypes.shape({
      nombre: PropTypes.string,
    }),
  ),
  onGenerated: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

GenerarResumenEjecutivoButton.defaultProps = {
  titulo: "",
  descripcion: "",
  antecedentes: undefined,
  resumenEjecutivo: undefined,
  fases: [],
  montoTotal: undefined,
  duracionEstimada: undefined,
  nombreCliente: undefined,
  empresaCliente: undefined,
  clientes: [],
  disabled: false,
};

export default GenerarResumenEjecutivoButton;
